<script setup>
// One numbered step of the Max's script, as a section of the document: what the step is for (goal), what the Max
// may say, what it asks and how it recognizes the answer, what it must not do, whether it listens and waits, the
// tools it may use, where the conversation goes next and on which channels. Each step saves on its own (the Save
// button of its header) and is discarded on its own; the page is told when a step has unsaved changes.
// A field with an error from the server (or from the validation of the whole script) shows it in red, and the step
// gets a red dot in its header so that the error does not hide in a folded step.
import { computed, reactive, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronDown16 } from '@carbon/icons-vue';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'dashboard/components-next/polaris/ui/collapsible';
import { FieldError } from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Label } from 'dashboard/components-next/polaris/ui/label';
import { Switch } from 'dashboard/components-next/polaris/ui/switch';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import MultiSelectInput from 'dashboard/components-next/polaris/cards/MultiSelectInput.vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import ScenarioAnswerHint from './ScenarioAnswerHint.vue';
import ScenarioNextRows from './ScenarioNextRows.vue';
import ScenarioStringList from './ScenarioStringList.vue';
import { errorsFor, hasErrors, isDirty, toForm, toNode } from './scenarioStep';

const props = defineProps({
  // { id, node_key, title, position, node, errors }
  record: { type: Object, required: true },
  index: { type: Number, required: true },
  // [{ value: node_key, label: title }]: every step, to choose where to go next
  steps: { type: Array, required: true },
  catalog: { type: Object, required: true },
  validation: { type: Object, default: () => ({ valid: true, errors: [] }) },
  saving: { type: Boolean, default: false },
  isEntry: { type: Boolean, default: false },
});
const emit = defineEmits(['save', 'dirty']);
const open = defineModel('open', { type: Boolean, default: false });

const { t } = useI18n();
const uid = useId();
const CHANNELS = ['voice', 'whatsapp'];

const title = ref('');
const form = reactive(toForm());
const reset = () => {
  title.value = props.record.title || '';
  Object.assign(form, toForm(props.record.node));
};
watch(() => props.record, reset, { immediate: true, deep: true });

const dirty = computed(() => isDirty(form, title.value, props.record));
watch(dirty, value => emit('dirty', props.record.id, value), {
  immediate: true,
});

const fieldErrors = field =>
  errorsFor(props.record, props.validation, field).map(error => error.message);
const failing = computed(() => hasErrors(props.record, props.validation));
// A step with an error opens itself, so that no error stays hidden.
watch(
  failing,
  value => {
    if (value) open.value = true;
  },
  { immediate: true }
);

// Facts of the step: the ones it asks about, the required ones and the optional ones, once each.
const facts = computed(() => [
  ...new Set([
    ...form.requiredFacts,
    ...form.optionalFacts,
    ...Object.keys(form.questions),
  ]),
]);
const factLabel = key =>
  props.catalog.facts?.find(fact => fact.key === key)?.label || key;
const addableFacts = computed(() =>
  (props.catalog.facts || [])
    .filter(fact => !facts.value.includes(fact.key))
    .map(fact => ({ value: fact.key, label: fact.label || fact.key }))
);
const isRequired = key => form.requiredFacts.includes(key);
const setRequired = (key, required) => {
  form.requiredFacts = form.requiredFacts.filter(item => item !== key);
  form.optionalFacts = form.optionalFacts.filter(item => item !== key);
  (required ? form.requiredFacts : form.optionalFacts).push(key);
};
const addFact = key => {
  if (!key) return;
  form.questions[key] = { ask: [''], answerHint: '' };
  setRequired(key, false);
};
const removeFact = key => {
  delete form.questions[key];
  form.requiredFacts = form.requiredFacts.filter(item => item !== key);
  form.optionalFacts = form.optionalFacts.filter(item => item !== key);
};

const tools = computed(() =>
  (props.catalog.tools || []).map(tool => ({
    value: tool.id,
    label: tool.label || tool.id,
  }))
);

const save = () => {
  if (!dirty.value || props.saving) return;
  emit('save', {
    id: props.record.id,
    title: title.value.trim(),
    node: toNode(form, props.record.node),
  });
};

defineExpose({ discard: reset, dirty });
</script>

<template>
  <Collapsible
    v-model:open="open"
    as="section"
    class="rounded-polaris-xl border border-solid border-polaris-border bg-polaris-card"
    :data-scenario-step="record.node_key"
    :data-step-dirty="dirty || undefined"
  >
    <div class="flex items-center gap-3 p-3">
      <CollapsibleTrigger
        class="flex min-w-0 flex-1 items-center gap-3 rounded-polaris-md p-1 text-start outline-none focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50"
        :aria-label="
          t(
            open
              ? 'POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEP.FOLD'
              : 'POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEP.UNFOLD',
            { title: title || record.node_key }
          )
        "
      >
        <span
          class="flex size-7 shrink-0 items-center justify-center rounded-full bg-polaris-muted type-polaris-body-medium"
          aria-hidden="true"
        >
          {{ index + 1 }}
        </span>
        <span class="min-w-0 flex-1 truncate type-polaris-card-title">
          {{ title || t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEP.UNTITLED') }}
        </span>
        <Badge v-if="isEntry" variant="secondary">
          {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEP.ENTRY') }}
        </Badge>
        <span
          v-if="failing"
          class="flex shrink-0 items-center gap-1 type-polaris-caption text-polaris-destructive"
          data-step-error
        >
          <span class="size-2 rounded-full bg-polaris-destructive" />
          {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEP.HAS_ERRORS') }}
        </span>
        <span
          v-else-if="dirty"
          class="shrink-0 type-polaris-caption text-polaris-muted-foreground"
        >
          {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEP.UNSAVED') }}
        </span>
        <ChevronDown16
          class="shrink-0 transition-transform duration-150"
          :class="open ? 'rotate-180' : ''"
          aria-hidden="true"
        />
      </CollapsibleTrigger>
      <div v-if="dirty" class="flex shrink-0 items-center gap-2">
        <Button type="button" variant="ghost" size="sm" @click="reset">
          {{ t('POLARIS.DRAWER.DISCARD') }}
        </Button>
        <Button
          type="button"
          size="sm"
          :disabled="saving"
          data-step-save
          @click="save"
        >
          {{ t('POLARIS.DRAWER.SAVE') }}
        </Button>
      </div>
    </div>
    <CollapsibleContent>
      <div
        class="grid gap-6 border-0 border-t border-solid border-polaris-border p-4"
      >
        <div class="grid gap-2">
          <Label :for="`${uid}-title`">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEP.TITLE') }}
          </Label>
          <Input :id="`${uid}-title`" v-model="title" />
        </div>

        <div class="grid gap-2" data-field="goal">
          <Label :for="`${uid}-goal`">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.GOAL.LABEL') }}
          </Label>
          <Textarea
            :id="`${uid}-goal`"
            v-model="form.goal"
            :aria-invalid="fieldErrors('goal').length > 0 || undefined"
            class="min-h-20"
          />
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.GOAL.HELP') }}
          </p>
          <FieldError :errors="fieldErrors('goal')" />
        </div>

        <div class="grid gap-2" data-field="say">
          <p class="type-polaris-body-medium">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.SAY.LABEL') }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.SAY.HELP') }}
          </p>
          <ScenarioStringList
            v-model="form.say"
            multiline
            :label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.SAY.LABEL')"
            :add-label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.SAY.ADD')"
            :invalid="fieldErrors('say').length > 0"
          />
          <FieldError :errors="fieldErrors('say')" />
        </div>

        <div class="grid gap-3" data-field="questions">
          <p class="type-polaris-body-medium">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.LABEL') }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.HELP') }}
          </p>
          <div
            v-for="key in facts"
            :key="key"
            class="grid gap-3 rounded-polaris-md border border-solid border-polaris-border p-3"
            :data-fact="key"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="type-polaris-body-medium">{{ factLabel(key) }}</p>
              <div class="flex items-center gap-3">
                <span class="flex items-center gap-2">
                  <Switch
                    :id="`${uid}-${key}-required`"
                    :model-value="isRequired(key)"
                    @update:model-value="value => setRequired(key, value)"
                  />
                  <Label :for="`${uid}-${key}-required`">
                    {{
                      t(
                        'POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.REQUIRED'
                      )
                    }}
                  </Label>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  @click="removeFact(key)"
                >
                  {{
                    t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.REMOVE')
                  }}
                </Button>
              </div>
            </div>
            <ScenarioStringList
              v-model="form.questions[key].ask"
              :label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.ASK')"
              :add-label="
                t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.ADD_ASK')
              "
            />
            <Collapsible class="grid gap-2">
              <CollapsibleTrigger
                class="w-fit rounded-polaris-md type-polaris-caption text-polaris-muted-foreground underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50"
              >
                {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HINT.ADVANCED') }}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ScenarioAnswerHint
                  :id="`${uid}-${key}`"
                  v-model="form.questions[key].answerHint"
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
          <div v-if="addableFacts.length" class="max-w-80">
            <OptionSelect
              model-value=""
              :options="addableFacts"
              :placeholder="
                t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.ADD_FACT')
              "
              :aria-label="
                t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.QUESTIONS.ADD_FACT')
              "
              size="sm"
              @update:model-value="addFact"
            />
          </div>
          <FieldError :errors="fieldErrors('questions')" />
          <FieldError :errors="fieldErrors('required_facts')" />
        </div>

        <div class="grid gap-2" data-field="avoid">
          <p class="type-polaris-body-medium">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.AVOID.LABEL') }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.AVOID.HELP') }}
          </p>
          <ScenarioStringList
            v-model="form.avoid"
            :label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.AVOID.LABEL')"
            :add-label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.AVOID.ADD')"
          />
          <FieldError :errors="fieldErrors('avoid')" />
        </div>

        <div class="grid gap-3" data-field="listen">
          <div class="flex items-center gap-3">
            <Switch :id="`${uid}-listen`" v-model="form.listen" />
            <Label :for="`${uid}-listen`">
              {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.LISTEN.LABEL') }}
            </Label>
          </div>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.LISTEN.HELP') }}
          </p>
          <div class="grid max-w-48 gap-2">
            <Label :for="`${uid}-silence`">
              {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.LISTEN.SILENCE') }}
            </Label>
            <Input
              :id="`${uid}-silence`"
              v-model="form.silenceTimeoutS"
              type="number"
              min="0"
              step="1"
              :disabled="!form.listen"
              :aria-invalid="
                fieldErrors('silence_timeout_s').length > 0 || undefined
              "
            />
          </div>
          <FieldError :errors="fieldErrors('listen')" />
          <FieldError :errors="fieldErrors('silence_timeout_s')" />
        </div>

        <div class="grid gap-2" data-field="tools">
          <p class="type-polaris-body-medium">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.TOOLS.LABEL') }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.TOOLS.HELP') }}
          </p>
          <MultiSelectInput
            v-model="form.tools"
            :options="tools"
            :label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.TOOLS.LABEL')"
            :placeholder="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.TOOLS.NONE')"
          />
          <FieldError :errors="fieldErrors('tools')" />
        </div>

        <div class="grid gap-2" data-field="next">
          <p class="type-polaris-body-medium">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.LABEL') }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.HELP') }}
          </p>
          <ScenarioNextRows
            v-model="form.next"
            :steps="steps"
            :predicates="catalog.predicates"
            :invalid="fieldErrors('next').length > 0"
          />
          <FieldError :errors="fieldErrors('next')" />
        </div>

        <div class="grid gap-2" data-field="channels">
          <p class="type-polaris-body-medium">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.CHANNELS.LABEL') }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.CHANNELS.HELP') }}
          </p>
          <div class="flex flex-wrap gap-4">
            <span
              v-for="channel in CHANNELS"
              :key="channel"
              class="flex items-center gap-2"
            >
              <Checkbox
                :id="`${uid}-ch-${channel}`"
                :model-value="form.channels.includes(channel)"
                @update:model-value="
                  value =>
                    (form.channels = value
                      ? [...form.channels, channel]
                      : form.channels.filter(item => item !== channel))
                "
              />
              <Label :for="`${uid}-ch-${channel}`">
                {{
                  t(
                    `POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.CHANNELS.${channel.toUpperCase()}`
                  )
                }}
              </Label>
            </span>
          </div>
          <FieldError :errors="fieldErrors('channels')" />
        </div>
      </div>
    </CollapsibleContent>
  </Collapsible>
</template>
